import { useEffect } from 'react';
import { useCreatorProfile } from './useCreatorProfile';
import { useUsernameContext } from '@/components/username/UsernameSetupProvider';
import { useTurnkeyWallet } from '@/providers/TurnkeyProvider';

/**
 * Hook to get a creator's display name.
 * Returns @username if set. Every registered user has a username
 * (mandatory on signup), so the fallback should rarely appear.
 */
export function useCreatorDisplayName(walletAddress: string | null) {
  const { profile, isLoading, refetch } = useCreatorProfile(walletAddress);
  const { currentUsername } = useUsernameContext();
  const { walletAddress: ownWallet } = useTurnkeyWallet();

  const isOwnWallet = !!walletAddress && walletAddress === ownWallet;

  // When own username was just set via setup flow, trigger background refetch
  useEffect(() => {
    if (isOwnWallet && currentUsername && !profile?.username) {
      refetch();
    }
  }, [isOwnWallet, currentUsername, profile?.username, refetch]);

  if (!walletAddress) {
    return { displayName: null, isLoading: false, username: null, walletAddress: null, xVerified: false, xTwitterUsername: null };
  }

  let displayName: string;
  if (profile?.username) {
    displayName = `@${profile.username}`;
  } else if (isOwnWallet && currentUsername) {
    // Username just set via setup flow, profile hasn't refetched yet
    displayName = `@${currentUsername}`;
  } else if (isLoading) {
    displayName = '...';
  } else {
    // Should not happen - every registered user has a username
    if (import.meta.env.DEV) {
      console.debug('[useCreatorDisplayName] Username missing for wallet:', walletAddress.slice(0, 8));
    }
    displayName = 'Unknown';
  }

  return {
    displayName,
    username: profile?.username || null,
    avatarUrl: profile?.avatar_url || null,
    avatarPath: profile?.avatar_path || null,
    avatarVersion: profile?.avatar_version ?? 0,
    xVerified: profile?.x_verified ?? false,
    xTwitterUsername: profile?.x_twitter_username ?? null,
    isLoading,
    walletAddress,
  };
}
